import { uploadWallImage } from "../../lib/cloud";
import type { WallBlock, WallBlockType } from "./types";
import { WALL_BLOCK_LABELS } from "./types";
import { WALL_DEFAULT_FACTS } from "./default-facts";

export type BlockEditorHandlers = {
  getData: () => Record<string, unknown>;
  patchData: (patch: Record<string, unknown>, opts?: { rerender?: boolean }) => void;
};

export function renderBlockEditorForm(block: WallBlock, handlers: BlockEditorHandlers): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "wallBlockEditorForm";

  const typeLabel = document.createElement("p");
  typeLabel.className = "wallBlockEditorType accessHint";
  typeLabel.textContent = WALL_BLOCK_LABELS[block.type] ?? block.type;
  wrap.appendChild(typeLabel);

  const setData = (key: string, value: unknown, rerender = false) => {
    handlers.patchData({ [key]: value }, rerender ? { rerender: true } : undefined);
  };

  const addText = (label: string, key: string, multiline = false) => {
    const row = document.createElement("label");
    row.className = "adminWallEditorField";
    row.textContent = label;
    const input = multiline ? document.createElement("textarea") : document.createElement("input");
    input.className = "formInput";
    if (input instanceof HTMLInputElement) input.type = "text";
    input.value = String(handlers.getData()[key] ?? "");
    input.oninput = () => setData(key, input.value);
    row.appendChild(input);
    wrap.appendChild(row);
  };

  const addNumber = (label: string, key: string, min = 0, max = 999999999) => {
    const row = document.createElement("label");
    row.className = "adminWallEditorField";
    row.textContent = label;
    const input = document.createElement("input");
    input.type = "number";
    input.className = "formInput";
    input.min = String(min);
    input.max = String(max);
    input.value = String(handlers.getData()[key] ?? min);
    input.oninput = () => setData(key, Math.min(max, Math.max(min, Number(input.value) || min)));
    row.appendChild(input);
    wrap.appendChild(row);
  };

  const addCheck = (label: string, key: string) => {
    const row = document.createElement("label");
    row.className = "adminWallEditorField adminWallEditorField--check";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = !!handlers.getData()[key];
    input.onchange = () => setData(key, input.checked);
    row.append(input, document.createTextNode(` ${label}`));
    wrap.appendChild(row);
  };

  switch (block.type as WallBlockType) {
    case "text":
      addText("Title (optional)", "title");
      addText("Body", "body", true);
      break;
    case "header":
      addText("Title", "title");
      addText("Subtitle (optional)", "subtitle");
      break;
    case "image":
      addNumber("Width %", "width", 20, 100);
      addText("Caption (optional)", "caption");
      {
        const fileRow = document.createElement("label");
        fileRow.className = "adminWallEditorField";
        fileRow.textContent = "Upload image";
        const file = document.createElement("input");
        file.type = "file";
        file.accept = "image/png,image/jpeg,image/webp,image/gif";
        file.onchange = () => {
          const f = file.files?.[0];
          if (!f) return;
          const reader = new FileReader();
          reader.onload = () => {
            void (async () => {
              try {
                const dataUrl = String(reader.result ?? "");
                const { id } = await uploadWallImage(f.type, dataUrl);
                setData("imageId", id);
              } catch {
                fileRow.appendChild(document.createTextNode(" Upload failed"));
              }
            })();
          };
          reader.readAsDataURL(f);
        };
        fileRow.appendChild(file);
        wrap.appendChild(fileRow);
      }
      break;
    case "quote":
      addText("Quote", "text", true);
      break;
    case "news":
      addText("Title", "title");
      addNumber("Limit", "limit", 1, 50);
      break;
    case "button":
      addText("Label", "label");
      addCheck("Confirm before action", "confirm");
      addText("Confirm text", "confirmText");
      addText("Link section (if action=link)", "linkSection");
      break;
    case "globalCounter":
      addText("Section label (optional)", "label");
      addText("Button text", "buttonText");
      addNumber("Cooldown (sec)", "cooldownSec", 0, 3600);
      addCheck("Show counter", "showCount");
      break;
    case "poll":
      addText("Question", "question");
      {
        const opts = Array.isArray(handlers.getData().options)
          ? [...(handlers.getData().options as string[])]
          : ["A", "B"];
        const list = document.createElement("div");
        list.className = "adminWallPollOpts";
        opts.forEach((opt, i) => {
          const inp = document.createElement("input");
          inp.className = "formInput";
          inp.value = opt;
          inp.placeholder = `Option ${i + 1}`;
          inp.oninput = () => {
            const next = Array.isArray(handlers.getData().options)
              ? [...(handlers.getData().options as string[])]
              : [...opts];
            next[i] = inp.value;
            setData("options", next);
          };
          list.appendChild(inp);
        });
        const addBtn = document.createElement("button");
        addBtn.type = "button";
        addBtn.className = "btn";
        addBtn.textContent = "+ Option";
        addBtn.onclick = () => {
          const next = Array.isArray(handlers.getData().options)
            ? [...(handlers.getData().options as string[])]
            : [...opts];
          setData("options", [...next, ""], true);
        };
        wrap.appendChild(list);
        wrap.appendChild(addBtn);
      }
      break;
    case "progress":
      addText("Title", "title");
      addNumber("Goal", "goal", 1);
      addText("Counter block id (optional)", "counterBlockId");
      break;
    case "stat":
      addText("Title", "title");
      addCheck("Show total games", "showGames");
      addCheck("Show total deaths", "showDeaths");
      addCheck("Show total points", "showPoints");
      addCheck("Show registered users", "showUsers");
      addCheck("Show playtime", "showPlaytime");
      break;
    case "playerSpotlight":
      addText("Title", "title");
      break;
    case "featuredPreset":
      addText("Title", "title");
      addNumber("Limit", "limit", 1, 20);
      break;
    case "featuredSpeedrun":
      addText("Title", "title");
      addNumber("Limit", "limit", 1, 20);
      break;
    case "randomFact":
      addText("Title", "title");
      addNumber("Refresh hint (sec)", "refreshSec", 5, 600);
      {
        const facts = Array.isArray(handlers.getData().facts)
          ? (handlers.getData().facts as string[]).filter((x) => typeof x === "string")
          : [];
        const listLabel = document.createElement("p");
        listLabel.className = "accessHint";
        listLabel.textContent = "Custom facts (one random entry shown). Empty list uses defaults.";
        wrap.appendChild(listLabel);
        const list = document.createElement("ul");
        list.className = "adminWallFactsList";
        facts.forEach((fact, i) => {
          const li = document.createElement("li");
          li.className = "adminWallFactsItem";
          li.textContent = fact;
          const del = document.createElement("button");
          del.type = "button";
          del.className = "btn adminWallBlockDel";
          del.textContent = "×";
          del.onclick = () => {
            const next = facts.filter((_, j) => j !== i);
            setData("facts", next, true);
          };
          li.appendChild(del);
          list.appendChild(li);
        });
        wrap.appendChild(list);
        const addRow = document.createElement("label");
        addRow.className = "adminWallEditorField";
        addRow.textContent = "Add fact";
        const factInput = document.createElement("textarea");
        factInput.className = "formInput";
        factInput.rows = 2;
        factInput.placeholder = "Type a fun fact…";
        const addFactBtn = document.createElement("button");
        addFactBtn.type = "button";
        addFactBtn.className = "btn green";
        addFactBtn.textContent = "Add fact";
        addFactBtn.onclick = () => {
          const text = factInput.value.trim();
          if (!text) return;
          const next = [...facts, text];
          factInput.value = "";
          setData("facts", next, true);
        };
        addRow.append(factInput, addFactBtn);
        wrap.appendChild(addRow);
        const defaultsBtn = document.createElement("button");
        defaultsBtn.type = "button";
        defaultsBtn.className = "btn";
        defaultsBtn.textContent = "Add Defaults";
        defaultsBtn.onclick = () => {
          const merged = [...facts];
          for (const d of WALL_DEFAULT_FACTS) {
            if (!merged.includes(d)) merged.push(d);
          }
          setData("facts", merged, true);
        };
        wrap.appendChild(defaultsBtn);
      }
      break;
    case "countdown":
      addText("Title", "title");
      addText("Target (ISO date)", "targetIso");
      addText("Done label", "label");
      break;
    case "link":
      addText("Button label", "label");
      addText("Section (home, rankings, stats, news, wall, settings…)", "section");
      break;
    default:
      wrap.appendChild(document.createTextNode("No editor fields for this block."));
  }

  return wrap;
}
